import { faEye, faEyeSlash, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";

import i18n from "../../../../constants/i18n";
import ET3Div from "../../../../core/et3-div";
import { withLiteObserverAndTheme } from "../../../../core/et3-enhanced-render";
import Et3Text from "../../../../core/et3-text";
import {
  PASSWORD_CARD_WIDTH,
  PASSWORD_FIELD_BORDER,
  PASSWORD_FIELD_ERROR_SHADOW,
  PASSWORD_FIELD_FOCUS_SHADOW,
  PASSWORD_FIELD_GAP,
  PASSWORD_FIELD_HEIGHT,
  PASSWORD_FIELD_ICON_SIZE,
  PASSWORD_FIELD_PADDING_X,
  PASSWORD_FIELD_RADIUS,
  PASSWORD_FIELD_SHADOW,
  PASSWORD_INPUT_FONT_SIZE,
  PASSWORD_LABEL_FONT_SIZE,
  PASSWORD_LABEL_GAP,
  PASSWORD_TRANSITION
} from "../change-password-constants";

const PASSWORD_INPUT_CLASS = "login-field-input";

type TPasswordField = {
  label: string;
  value: string;
  hasError: boolean;
  onChoose: (value: string) => void;
};

const PasswordField = withLiteObserverAndTheme<TPasswordField>((props) => {
  const { label, value, hasError, onChoose, theme } = props;
  const { colors, unitX, unitY, getFontSize, getFontType } = theme;
  const [isFocused, setIsFocused] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);

  const borderColor = (() => {
    switch (true) {
      case hasError:
        return colors.all.red;
      case isFocused:
        return colors.all.bondiBlue;
      default:
        return colors.all.shadeOfGray;
    }
  })();

  const fieldShadow = (() => {
    switch (true) {
      case hasError:
        return PASSWORD_FIELD_ERROR_SHADOW;
      case isFocused:
        return PASSWORD_FIELD_FOCUS_SHADOW;
      default:
        return PASSWORD_FIELD_SHADOW;
    }
  })();

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={PASSWORD_CARD_WIDTH}
      height={0}
      fDirection="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{
        width: "100%",
        minWidth: 0,
        gap: PASSWORD_LABEL_GAP * unitY
      }}
      onClick={() => {}}
    >
      <Et3Text
        fontFamily={"MEDIUM"}
        fontSize={PASSWORD_LABEL_FONT_SIZE}
        text={label}
        isSpan={false}
        color={colors.all.maastrichtBlue}
        style={{ margin: 0, textAlign: "left" }}
      />
      <ET3Div
        isScrollable={false}
        isExtendable={false}
        withShadow={false}
        width={PASSWORD_CARD_WIDTH}
        height={PASSWORD_FIELD_HEIGHT}
        fDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        color={colors.all.white}
        style={{
          boxSizing: "border-box",
          width: "100%",
          minWidth: 0,
          height: PASSWORD_FIELD_HEIGHT * unitY,
          borderRadius: PASSWORD_FIELD_RADIUS * unitX,
          border: `${PASSWORD_FIELD_BORDER} ${borderColor}`,
          boxShadow: fieldShadow,
          transition: PASSWORD_TRANSITION,
          paddingLeft: PASSWORD_FIELD_PADDING_X * unitX,
          paddingRight: PASSWORD_FIELD_PADDING_X * unitX,
          gap: PASSWORD_FIELD_GAP * unitX
        }}
        onClick={() => {}}
      >
        <FontAwesomeIcon
          icon={faLock}
          color={isFocused ? colors.all.bondiBlue : colors.all.gray}
          style={{
            width: PASSWORD_FIELD_ICON_SIZE * unitX,
            height: PASSWORD_FIELD_ICON_SIZE * unitY,
            flexShrink: 0,
            transition: PASSWORD_TRANSITION
          }}
        />
        <input
          type={isRevealed ? "text" : "password"}
          className={PASSWORD_INPUT_CLASS}
          aria-label={label}
          value={value}
          onChange={({ target }) => onChoose(target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            boxSizing: "border-box",
            alignSelf: "stretch",
            border: 0,
            padding: 0,
            backgroundColor: colors.all.transparent,
            color: colors.all.maastrichtBlue,
            fontFamily: getFontType("MEDIUM"),
            fontSize: getFontSize(PASSWORD_INPUT_FONT_SIZE),
            flexGrow: 1,
            minWidth: 0
          }}
        />
        <button
          type="button"
          aria-label={
            isRevealed ? i18n.get("HIDE_PASSWORD") : i18n.get("SHOW_PASSWORD")
          }
          onClick={() => setIsRevealed(!isRevealed)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: 0,
            padding: 0,
            cursor: "pointer",
            flexShrink: 0,
            backgroundColor: colors.all.transparent
          }}
        >
          <FontAwesomeIcon
            icon={isRevealed ? faEyeSlash : faEye}
            color={colors.all.dimGray}
            style={{
              width: PASSWORD_FIELD_ICON_SIZE * unitX,
              height: PASSWORD_FIELD_ICON_SIZE * unitY
            }}
          />
        </button>
      </ET3Div>
    </ET3Div>
  );
});
export default PasswordField;
